import {
  type CardReveal,
  type CardRevealClient,
  type CardRevealStyle,
  type WebRevealOptions,
  createCardRevealClient,
} from '@column-na/card-reveal';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import columnLogoWhite from '~/assets/column-logo-white.svg';
import columnSymbolWhite from '~/assets/column-symbol-white.svg';
import { CardAccountCard, CardProgramCard, CopyInput, EntityCard, Section } from '~/components';
import { API_URL } from '~/constants';
import { useNavigate } from '~/lib/navigation';
import { CardRepository } from '~/repositories/CardRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { CardProps } from './Card';

interface Params {
  id: string;
}

const cardQueryKey = (id: string) => ['card', id] as const;
const CARD_FONT_STACK = '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RevealContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
`;

const RevealCard = styled.div`
  position: relative;
  width: 320px;
  height: 180px;
  border-radius: 12px;
  background: #1e4199;
  color: white;
  font-family: ${CARD_FONT_STACK};
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(10, 10, 10, 0.22);
`;

const RevealBrandSymbol = styled.img`
  position: absolute;
  right: 20px;
  top: 20px;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const RevealBrandLogo = styled.img`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 80px;
  height: 18px;
  object-fit: contain;
`;

const RevealCardholderValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 180px;
  height: 18px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 180px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealExpires = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 42px;
  display: flex;
  align-items: center;
  gap: 0;
  font-family: ${CARD_FONT_STACK};
  font-size: 12px;
  text-transform: uppercase;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  color: ${({ $isVisible }) => ($isVisible ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.62)')};
  text-shadow: ${({ $isVisible }) => ($isVisible ? '0 0 8px rgba(255, 255, 255, 0.32)' : 'none')};
  transition:
    opacity 120ms ease,
    color 120ms ease,
    text-shadow 120ms ease;
`;

const RevealMetaLabel = styled.span`
  display: inline-block;
  width: 44px;
`;

const RevealExpValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 52px;
  gap: 2px;
`;

const RevealExpMonthValue = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: 16px;
  height: 18px;
  vertical-align: middle;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 16px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealExpYearValue = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: 16px;
  height: 18px;
  vertical-align: middle;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 16px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealCvvValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 20px;
  height: 18px;
  display: flex;
  align-items: center;
  gap: 0;
  font-family: ${CARD_FONT_STACK};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  color: ${({ $isVisible }) => ($isVisible ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.62)')};
  text-shadow: ${({ $isVisible }) => ($isVisible ? '0 0 8px rgba(255, 255, 255, 0.32)' : 'none')};
  transition:
    opacity 120ms ease,
    color 120ms ease,
    text-shadow 120ms ease;

  iframe {
    width: 52px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 78px;
  left: 20px;
  width: 280px;
  height: 24px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 280px;
    height: 24px;
    border: 0;
    background: transparent;
  }
`;

const RevealOverlayButton = styled.button`
  position: absolute;
  inset: 0;
  z-index: 2;
  border: none;
  background: rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: background-color 0.16s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.28);
  }
`;

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

// Created on first use: only sandbox card pages reveal cards. Sandbox reveals call the simulate reveal route on the
// API this dashboard uses (API_URL), so staging and local dev reveal their own cards.
let cardRevealClient: CardRevealClient | null = null;
const getCardRevealClient = (): CardRevealClient =>
  (cardRevealClient ??= createCardRevealClient({
    environment: 'sandbox',
    sandboxBaseUrl: API_URL,
  }));

const REVEAL_FIELD_STYLE: CardRevealStyle = {
  'font-family': CARD_FONT_STACK,
  fontSize: '12px',
  fontWeight: '500',
  lineHeight: '16px',
  color: 'white',
  display: 'block',
  background: 'transparent',
};

const REVEAL_FIELD_STYLES: WebRevealOptions['fieldStyles'] = {
  pan: { fontSize: '19px', lineHeight: '24px', letterSpacing: '1px', whiteSpace: 'pre' },
  cardholder_name: { textTransform: 'uppercase' },
};

type RevealState = 'idle' | 'pending' | 'ready';

const formatType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

export const PageCardInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { isSandbox } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const { card: outletCard } = useOutletContext<CardProps>();
  const [revealState, setRevealState] = useState<RevealState>('idle');
  const revealRef = useRef<CardReveal | null>(null);
  // Bumped on every reveal attempt and card change, so a continuation from an earlier attempt can tell it is stale.
  const revealAttemptRef = useRef(0);

  const { data: card, error: cardError } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id && !outletCard,
    initialData: outletCard,
  });

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  const cardId = card?.id;

  useEffect(
    () => () => {
      revealAttemptRef.current += 1;
      revealRef.current?.unmount();
      revealRef.current = null;
      setRevealState('idle');
    },
    [cardId]
  );

  if (!card) {
    return null;
  }

  const containerIds = {
    pan: `card-pan-reveal-${card.id}`,
    cvv: `card-cvv-reveal-${card.id}`,
    cardholder_name: `card-cardholder-reveal-${card.id}`,
    expiration_month: `card-exp-month-reveal-${card.id}`,
    expiration_year: `card-exp-year-reveal-${card.id}`,
  };

  const handleReveal = async () => {
    if (!isSandbox || revealState !== 'idle') {
      return;
    }

    const attempt = ++revealAttemptRef.current;
    const isStale = () => attempt !== revealAttemptRef.current;
    setRevealState('pending');
    try {
      const client = getCardRevealClient();
      const revealToken = await CardRepository.createRevealToken(card.id);
      if (isStale()) {
        return;
      }
      const reveal = client.reveal({
        cardId: card.id,
        revealToken: revealToken.id,
        fields: {
          pan: `#${containerIds.pan}`,
          cvv: `#${containerIds.cvv}`,
          cardholder_name: `#${containerIds.cardholder_name}`,
          expiration_month: `#${containerIds.expiration_month}`,
          expiration_year: `#${containerIds.expiration_year}`,
        },
        style: REVEAL_FIELD_STYLE,
        fieldStyles: REVEAL_FIELD_STYLES,
        format: { expirationYear: 'YY' },
      });
      revealRef.current = reveal;
      await reveal.ready;
      if (!isStale()) {
        setRevealState('ready');
      }
    } catch (error: any) {
      if (isStale()) {
        return;
      }
      revealRef.current?.unmount();
      revealRef.current = null;
      setRevealState('idle');
      addDangerNotification({
        content: error?.message || 'Failed to reveal card',
      });
    }
  };

  const isRevealReady = revealState === 'ready';
  return (
    <Inner p={0}>
      {isSandbox && (
        <Section>
          <Inner px="var(--page-margin)" py={16}>
            <RevealContainer>
              <RevealCard>
                {!isRevealReady && (
                  <RevealOverlayButton
                    type="button"
                    onClick={handleReveal}
                    disabled={revealState === 'pending'}
                    aria-label="Reveal card"
                  >
                    {revealState === 'pending' ? 'Revealing...' : 'Click to Reveal'}
                  </RevealOverlayButton>
                )}
                <RevealValue id={containerIds.pan} $isVisible={isRevealReady} />
                <RevealBrandLogo src={columnLogoWhite} alt="Column logo" />
                <RevealBrandSymbol src={columnSymbolWhite} alt="Column symbol" />
                <RevealCardholderValue id={containerIds.cardholder_name} $isVisible={isRevealReady} />
                <RevealExpires $isVisible={isRevealReady}>
                  <RevealMetaLabel>EXP</RevealMetaLabel>
                  <RevealExpValue>
                    <RevealExpMonthValue id={containerIds.expiration_month} $isVisible={isRevealReady} />
                    <span>/</span>
                    <RevealExpYearValue id={containerIds.expiration_year} $isVisible={isRevealReady} />
                  </RevealExpValue>
                </RevealExpires>
                <RevealCvvValue $isVisible={isRevealReady}>
                  <RevealMetaLabel>CVV2</RevealMetaLabel>
                  <span id={containerIds.cvv} />
                </RevealCvvValue>
              </RevealCard>
            </RevealContainer>
          </Inner>
        </Section>
      )}
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <FieldsContainer>
            <FieldRow>
              <FieldLabel>Description</FieldLabel>
              <FieldValue>
                <CopyInput value={card.description || '-'} />
              </FieldValue>
            </FieldRow>

            {card.authorizedUserEntityId && (
              <FieldRow>
                <FieldLabel>Authorized User</FieldLabel>
                <FieldValue>
                  <EntityCard entityId={card.authorizedUserEntityId} />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>Card Program</FieldLabel>
              <FieldValue>
                {card.cardProgramId ? <CardProgramCard cardProgramId={card.cardProgramId} /> : <CopyInput value="-" />}
              </FieldValue>
            </FieldRow>

            {card.cardAccountId && (
              <FieldRow>
                <FieldLabel>Card Account</FieldLabel>
                <FieldValue>
                  <CardAccountCard cardAccountId={card.cardAccountId} />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>BIN</FieldLabel>
              <FieldValue>
                <CopyInput value={card.bin || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Last Four Digits</FieldLabel>
              <FieldValue>
                <CopyInput value={card.lastFourDigits || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Expires</FieldLabel>
              <FieldValue>
                <CopyInput value={card.expiresAt || '-'} />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Status</FieldLabel>
              <FieldValue>
                <StatusChip $status={card.status || ''}>{formatStatus(card.status || '')}</StatusChip>
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Type</FieldLabel>
              <FieldValue>
                <TypeChip>{formatType(card.type || '')}</TypeChip>
              </FieldValue>
            </FieldRow>

            {card.createdAt && (
              <FieldRow>
                <FieldLabel>Created At</FieldLabel>
                <FieldValue>
                  <CopyInput value={formatDateTime(card.createdAt, { timeZoneFormat: 'utc' })} />
                </FieldValue>
              </FieldRow>
            )}
          </FieldsContainer>
        </Inner>
      </Section>
    </Inner>
  );
};
